/**
 * ReviewSection Component
 * Collapsible section for adding a text review
 */

import { Text } from '@/components/ui/text';
import { TextInput } from '@/components/ui/text-input';
import Ionicons from '@expo/vector-icons/Ionicons';
import React, { useCallback, useState } from 'react';
import { View } from 'react-native';

export interface ReviewSectionProps {
  /** Current review text */
  value: string;
  /** Callback when review text changes */
  onChange: (text: string) => void;
  /** Maximum character count */
  maxLength?: number;
  /** Placeholder text */
  placeholder?: string;
  /** Whether the section is disabled */
  disabled?: boolean;
}

function getCharacterCountClassName(count: number, max: number): string {
  if (count >= max) return 'text-xs text-error';
  if (count > max * 0.8) return 'text-xs text-warning';
  return 'text-xs text-muted-foreground';
}

export function ReviewSection({
  value,
  onChange,
  maxLength = 500,
  placeholder = 'Share your thoughts about this dish...',
  disabled = false,
}: ReviewSectionProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const characterCount = value.length;

  return (
    <View className="mb-4">
      {/* Header Row */}
      <View
        className="flex-row items-center justify-between pt-3"
      >
        <View className="flex-row items-center">
          <Text className="text-xl text-foreground">
            Review
          </Text>
          {value.length > 0 && (
            <View className="ml-2 px-2 py-0.5 rounded-full bg-primary/[0.13]">
              <Ionicons
                name="checkmark"
                size={12}
                className="text-primary"
              />
            </View>
          )}
        </View>
      </View>

      {/* Expanded Content */}
      {isExpanded && (
        <View className="mt-2">
          <TextInput
            value={value}
            onChangeText={onChange}
            placeholder={placeholder}
            multiline
            maxLength={maxLength}
            editable={!disabled}
            accessibilityLabel="Review text input"
            accessibilityHint={`Enter your review, maximum ${maxLength} characters`}
          />

          {/* Character Count */}
          <View className="flex-row justify-end mt-1">
            <Text className={getCharacterCountClassName(characterCount, maxLength)}>
              {characterCount}/{maxLength}
            </Text>
          </View>
        </View>
      )}
    </View>
  );
}

export default ReviewSection;
